import { OneOfElements } from '@shared/models/project.model';
import {
  areProductOverridesEffectivelyEqual,
  mergeProductOverridePatch,
} from '@shared/helpers/product-element.helpers';
import {
  productElementCarbonLevelChangeToOverridePatch,
  productElementTransportChangeToOverridePatch,
  productElementWasteChangeToOverridePatch,
  type ProductElementCarbonLevelOverrideChange,
  type ProductElementTransportOverrideChange,
  type ProductElementWasteOverrideChange,
} from '@shared/helpers/pipeline/product-element-override-changes.helpers';
import { projectGuards } from '../../guards/project-guards.helpers';
import type { PartialProductElementOverrides } from './product-element-override-patch.types';

/**
 * Applies a partial override patch to a product element.
 */
const productElementOverridePatchToPartial = (
  element: OneOfElements,
  patch: PartialProductElementOverrides,
): Partial<OneOfElements> | undefined => {
  if (!projectGuards.is.productElement(element)) return undefined;

  const merged = mergeProductOverridePatch(element.productOverrides, patch);

  if (areProductOverridesEffectivelyEqual(element.productOverrides, merged)) {
    return undefined;
  }

  return { productOverrides: merged };
};

/**
 * Applies {@link ProjectChangeType.ProductElementTransportOverride}.
 */
export const productElementTransportOverrideToChange = (
  element: OneOfElements,
  change: ProductElementTransportOverrideChange,
): Partial<OneOfElements> | undefined =>
  productElementOverridePatchToPartial(
    element,
    productElementTransportChangeToOverridePatch(change),
  );

/**
 * Applies {@link ProjectChangeType.ProductElementWasteOverride}.
 */
export const productElementWasteOverrideToChange = (
  element: OneOfElements,
  change: ProductElementWasteOverrideChange,
): Partial<OneOfElements> | undefined =>
  productElementOverridePatchToPartial(
    element,
    productElementWasteChangeToOverridePatch(change),
  );

/**
 * Applies {@link ProjectChangeType.ProductElementCarbonLevelOverride}.
 */
export const productElementCarbonLevelOverrideToChange = (
  element: OneOfElements,
  change: ProductElementCarbonLevelOverrideChange,
): Partial<OneOfElements> | undefined =>
  productElementOverridePatchToPartial(
    element,
    productElementCarbonLevelChangeToOverridePatch(change),
  );
